<template>
  <LcTable v-if="prizeTier" class="prize-tier-modal-table" padding="none">
    <thead v-if="hasHeader">
      <tr>
        <th></th>
        <th
          v-for="column in columns"
          :key="column.key"
          :class="{ 'prize-tier-modal-table-highlight': column.paysOnTop }"
        >
          <div class="flex gap-025 items-center justify-center">
            <LcImage v-if="column.image" class="w-300 h-300" v-bind="column.image" alt="" />
            {{ column.label }}
          </div>
        </th>
      </tr>
      <tr class="prize-tier-modal-table-header-spacer"></tr>
    </thead>

    <tbody>
      <tr v-for="matchCount in matchCounts" :key="matchCount">
        <td>{{ t('highfrequencygame.prizemodaltable.body.match', { value: matchCount }) }}</td>
        <td
          v-for="column in columns"
          :key="column.key"
          :class="{ 'prize-tier-modal-table-highlight': column.paysOnTop }"
        >
          {{ prizeCell(column, matchCount) }}
        </td>
      </tr>
      <tr class="text-secondary-secondary">
        <td>{{ t('highfrequencygame.prizemodaltable.header.chance') }}</td>
        <td
          v-for="column in columns"
          :key="column.key"
          :class="{ 'prize-tier-modal-table-highlight': column.paysOnTop }"
        >
          {{
            t('highfrequencygame.prizemodaltable.header.chancevalue', {
              value: n(column.overallChance),
            })
          }}
        </td>
      </tr>
    </tbody>
  </LcTable>
</template>

<script lang="ts" setup>
import type { PrizeTierCategory } from '@lottocom/high-frequency-prize-tiers'

import { getHfgAddOnIllustration } from './highFrequencyGameConstants'

const { gameId, prizeTier } = useHighFrequencyGame()
const { formatPrizeMoney } = usePrizeMoneyFormatter()
const { n, t, te } = useI18n()
const { getIllustrationMini } = useAsset()

const getAddOnImage = (addOn: PrizeTierCategory['addOn']) => {
  const illustration = addOn ? getHfgAddOnIllustration(addOn) : null
  return illustration ? getIllustrationMini(illustration) : null
}

const defaultLabel = computed(() => {
  const gameKey = `highfrequencygame.prizemodaltable.header.default.${gameId}`
  return te(gameKey) ? t(gameKey) : t('highfrequencygame.prizemodaltable.header.default')
})

const columns = computed(() =>
  (prizeTier.value?.prizeTable ?? [])
    .filter(({ selected }) => selected)
    .map((category) => ({
      addOn: category.addOn,
      image: category.paysOnTop ? getAddOnImage(category.addOn) : null,
      key: category.name,
      label: category.addOn ? t(`highfrequencygame.addon.${category.addOn}`) : defaultLabel.value,
      overallChance: category.overallChance,
      paysOnTop: category.paysOnTop,
      prizeTiers: category.prizeTiers,
    })),
)

const hasHeader = computed(() => columns.value.length > 1)

const matchCounts = computed(() =>
  R_pipe(
    columns.value.flatMap(({ prizeTiers }) => prizeTiers.map(({ matchCount }) => matchCount)),
    R_unique(),
    R_sortBy([(matchCount) => matchCount, 'desc']),
  ),
)

const prizeCell = (
  { paysOnTop, prizeTiers }: Pick<PrizeTierCategory, 'paysOnTop' | 'prizeTiers'>,
  matchCount: number,
) => {
  const tier = prizeTiers.find((tier) => tier.matchCount === matchCount)
  if (!tier) return t('highfrequencygame.prizemodaltable.body.empty')

  const parts = [formatPrizeMoney(tier.prize)]
  if (paysOnTop) parts.unshift(t('tiertable.fielddivider'))
  if (tier.jackpot) parts.push(t('highfrequencygame.prizemodaltable.body.jackpot'))
  return parts.join(' ')
}
</script>

<style lang="scss" scoped>
.prize-tier-modal-table {
  &-header-spacer {
    height: token('size/space/gap/100');
  }

  &-highlight {
    background-color: token('color/background/secondary/secondary-bold');

    &:is(th) {
      border-radius: token('size/radius/s');
    }
  }

  tr {
    @include token-paragraph-bold;
  }

  thead {
    tr:first-child {
      height: 1.75rem;
    }
  }

  tbody {
    tr td {
      vertical-align: middle;
      padding: token('size/space/gap/050') token('size/space/gap/050') token('size/space/gap/100');

      &:first-child {
        white-space: nowrap;
      }

      &:not(:first-child) {
        text-align: right;
      }
    }

    tr:first-child td.prize-tier-modal-table-highlight {
      border-radius: token('size/radius/s') token('size/radius/s') token('size/radius/none')
        token('size/radius/none');
    }

    tr:last-child td {
      padding-bottom: token('size/space/gap/050');
      border-radius: token('size/radius/none') token('size/radius/none') token('size/radius/s')
        token('size/radius/s');
    }
  }

  tr > :is(th, td):nth-child(n + 2):nth-last-child(n + 2) {
    padding-right: clamp(1rem, 8.9vw, 3.5rem);

    > * {
      justify-content: end;
    }
  }
}
</style>
